"use client";

import { useEffect, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { createClient } from "@/lib/supabase/client";
import { recordPasswordLogin } from "@/lib/actions/auth";
import { buildCaptchaOptions } from "@/lib/auth/captcha";
import {
  PASSWORD_MIN_LENGTH,
  getAuthPasswordErrorMessage,
  getWeakPasswordSignInMessage,
  validatePasswordPolicy,
} from "@/lib/auth/password-policy";
import { ACTIVE_BRANDING, ACTIVE_BRAND_SLUG, type AuthPolicyConfig } from "@/lib/branding";
import { CaptchaWidget, type CaptchaWidgetRef } from "./auth/captcha-widget";
import { isPostHogEnabled } from "@/lib/posthog/env";
import { Button, Input, Label } from "@handled-ai/design-system";
import { AuthPageShell } from "./auth/auth-page-shell";
import { postSessionContext } from "./auth/session-context-request";
import {
  AUTH_ERROR_MESSAGES,
  AUTH_ERRORS_THAT_CLEAR_LOCAL_SESSION,
  type PublicAuthErrorCode,
} from "@/lib/auth/auth-errors";

type AuthMode = "login" | "signup";
type AuthMethod = "google" | "password";

const DEFAULT_AUTH_POLICY: AuthPolicyConfig = {
  primaryMethod: "password",
  showPasswordLogin: true,
  allowPasswordEscapeHatch: true,
};

function publicAuthErrorCode(value: string): PublicAuthErrorCode | null {
  return value in AUTH_ERROR_MESSAGES ? (value as PublicAuthErrorCode) : null;
}

export function captureAuthEvent(
  eventName: string,
  properties: Record<string, unknown>,
  attempt = 0,
): void {
  if (!isPostHogEnabled()) return;

  void import("posthog-js").then(({ default: posthog }) => {
    if (!posthog.__loaded) {
      if (attempt < 5) {
        globalThis.setTimeout(
          () => captureAuthEvent(eventName, properties, attempt + 1),
          250,
        );
      }
      return;
    }
    posthog.capture(eventName, properties);
  });
}

interface AuthFormProps {
  mode: AuthMode;
  /** Server-side error code passed via ?error= query param (e.g. "no_tenant") */
  authError?: string;
  invited?: boolean;
  workspace?: string;
}

const SESSION_CONTEXT_REQUEST_ERROR_MESSAGE =
  "Could not enter this workspace. Please try again.";

const config = {
  login: {
    subtitle: "Enter your credentials to access your workspace",
    submitLabel: "Sign in",
    loadingLabel: "Signing in...",
    altText: "Don’t have an account?",
    altLink: "/signup",
    altLinkLabel: "Sign up",
  },
  signup: {
    subtitle: "Create your account to get started",
    submitLabel: "Sign up",
    loadingLabel: "Signing up...",
    altText: "Already have an account?",
    altLink: "/login",
    altLinkLabel: "Sign in",
  },
} as const;

type AuthErrorLike = {
  code?: string | null;
  status?: number | null;
  message?: string | null;
};

function shouldNormalizeSignupOutcome(error: AuthErrorLike | null): boolean {
  if (!error) return true;

  const code = error.code?.toLowerCase();
  if (code === "user_already_exists" || code === "email_exists" || code === "over_email_send_rate_limit") {
    return true;
  }

  const message = error.message?.toLowerCase() ?? "";
  return (
    /already (registered|exists)|user already|email.*exists/.test(message) ||
    (message.includes("email") && message.includes("rate limit"))
  );
}

function AuthErrorMessages({
  serverError,
  error,
}: {
  serverError: string | null;
  error: string | null;
}) {
  if (!serverError && !error) return null;

  return (
    <div className="space-y-2" data-testid="login-error">
      {serverError && (
        <div className="rounded-md bg-amber-50 border border-amber-200 p-3">
          <p className="text-sm text-amber-800">{serverError}</p>
        </div>
      )}
      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );
}

function AuthBrandLogo() {
  const loginLogo = ACTIVE_BRANDING.splash?.iconMark ?? ACTIVE_BRANDING.logo;
  const loginLogoClass = ACTIVE_BRANDING.splash?.iconMark
    ? "h-12 w-12"
    : "h-10 w-auto";

  return (
    <div className="flex justify-center">
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        src={loginLogo}
        alt={ACTIVE_BRANDING.logoAlt}
        className={loginLogoClass}
      />
    </div>
  );
}

export default function AuthForm({ mode, authError, invited = false, workspace }: AuthFormProps) {
  const router = useRouter();
  const [loading, setLoading] = useState(false);
  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [captchaToken, setCaptchaToken] = useState("");
  const [signupSuccess, setSignupSuccess] = useState(false);
  const captchaRef = useRef<CaptchaWidgetRef>(null);
  const authPolicy = ACTIVE_BRANDING.authPolicy ?? DEFAULT_AUTH_POLICY;
  const [showPasswordForm, setShowPasswordForm] = useState(
    mode === "signup" || authPolicy.showPasswordLogin,
  );

  const workspaceName = workspace?.trim() || null;
  const arrivedWithInviteContext = invited;
  const inviteWorkspaceName = arrivedWithInviteContext ? workspaceName : null;
  const shouldUseGoogleFirst = mode === "login" && authPolicy.primaryMethod === "google";
  const canRevealPasswordForm =
    mode === "login" &&
    !authPolicy.showPasswordLogin &&
    authPolicy.allowPasswordEscapeHatch;
  const passwordFormIsEscapeHatch = canRevealPasswordForm && showPasswordForm;
  const showDivider = showPasswordForm && (mode === "signup" || authPolicy.showPasswordLogin);
  const googleButtonLabel = googleLoading
    ? "Redirecting..."
    : shouldUseGoogleFirst && inviteWorkspaceName
      ? `Continue with Google to join ${inviteWorkspaceName}`
      : "Continue with Google";

  const authErrorCode = authError ? publicAuthErrorCode(authError) : null;
  // Resolve server-side auth error from query param
  const serverError = authError
    ? authErrorCode ? AUTH_ERROR_MESSAGES[authErrorCode] : `Authentication error: ${authError}`
    : null;

  useEffect(() => {
    const shouldClearLocalSession = authErrorCode
      ? AUTH_ERRORS_THAT_CLEAR_LOCAL_SESSION.has(authErrorCode)
      : false;
    if (!shouldClearLocalSession) return;

    const supabase = createClient();
    void supabase.auth.signOut({ scope: "local" });
  }, [authErrorCode]);

  function captureLoginEvent(eventName: string, authMethod: AuthMethod): void {
    captureAuthEvent(eventName, {
      brand: ACTIVE_BRAND_SLUG,
      tenant: ACTIVE_BRAND_SLUG,
      arrived_with_invite_context: arrivedWithInviteContext,
      auth_method: authMethod,
    });
  }

  useEffect(() => {
    if (mode !== "login") return;

    captureLoginEvent("login_viewed", authPolicy.primaryMethod);
  }, [arrivedWithInviteContext, authPolicy.primaryMethod, mode]);

  const baseConfig = config[mode];
  const defaultTitle = mode === "login"
    ? `Sign in to ${ACTIVE_BRANDING.productName}`
    : `Sign up for ${ACTIVE_BRANDING.productName}`;
  const title = shouldUseGoogleFirst && inviteWorkspaceName
    ? `Join ${inviteWorkspaceName}`
    : defaultTitle;
  const subtitle = shouldUseGoogleFirst
    ? inviteWorkspaceName
      ? `Continue with Google to access ${inviteWorkspaceName}.`
      : `Continue with Google to access ${ACTIVE_BRANDING.productName}.`
    : baseConfig.subtitle;
  const { submitLabel, loadingLabel, altText, altLink, altLinkLabel } = baseConfig;

  async function handleGoogleSignIn() {
    setGoogleLoading(true);
    setError(null);
    if (mode === "login") {
      captureLoginEvent("login_method_selected", "google");
    }
    const supabase = createClient();
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: {
        redirectTo: `${window.location.origin}/auth/callback`,
      },
    });
    if (error) {
      setError(error.message);
      setGoogleLoading(false);
    }
    // If successful, the browser will redirect to Google
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setLoading(true);
    setError(null);

    const resetCaptcha = () => {
      setCaptchaToken("");
      captchaRef.current?.reset();
    };

    const formData = new FormData(e.currentTarget);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;
    const captchaOptions = buildCaptchaOptions(captchaToken);

    const supabase = createClient();

    if (mode === "login") {
      captureLoginEvent("login_method_selected", "password");

      const signInPayload = captchaOptions
        ? { email, password, options: captchaOptions }
        : { email, password };
      const { data, error } = await supabase.auth.signInWithPassword(signInPayload);
      resetCaptcha();
      if (error) {
        setError(getAuthPasswordErrorMessage(error, "login") ?? error.message);
        captureLoginEvent("password_login_failed", "password");
        if (arrivedWithInviteContext && passwordFormIsEscapeHatch) {
          captureLoginEvent("password_login_failed_no_credential", "password");
        }
        setLoading(false);
        return;
      }

      const weakPasswordMessage = getWeakPasswordSignInMessage(data);
      if (weakPasswordMessage) {
        try {
          const { error: signOutError } = await supabase.auth.signOut({ scope: "local" });
          if (signOutError) {
            console.error("[auth-form] Failed to clear weak-password session:", signOutError.message);
          }
        } catch (err) {
          console.error(
            "[auth-form] Failed to clear weak-password session:",
            err instanceof Error ? err.message : String(err),
          );
        }
        setError(weakPasswordMessage);
        setLoading(false);
        return;
      }
      const contextResponse = await postSessionContext();

      if (!contextResponse?.ok) {
        const body = await contextResponse?.json().catch(() => ({}));
        const code = typeof body?.error === "string" ? publicAuthErrorCode(body.error) : null;
        setError(code ? AUTH_ERROR_MESSAGES[code] : SESSION_CONTEXT_REQUEST_ERROR_MESSAGE);
        setLoading(false);
        return;
      }

      // Force the browser-side Supabase client to re-read from cookies so it
      // picks up the enriched access token (with tenant_id) that the server
      // wrote via refreshSession() in the session-context API route.
      await supabase.auth.getSession();

      try {
        await recordPasswordLogin();
      } catch (err) {
        console.error(
          "[auth-form] Failed to record password login activity:",
          err instanceof Error ? err.message : String(err),
        );
      }
      router.push("/inbox");
    } else {
      const policyError = validatePasswordPolicy(password);
      if (policyError) {
        setError(policyError);
        setLoading(false);
        return;
      }

      const signUpPayload = captchaOptions
        ? { email, password, options: captchaOptions }
        : { email, password };
      const { error } = await supabase.auth.signUp(signUpPayload);
      resetCaptcha();
      if (shouldNormalizeSignupOutcome(error)) {
        setSignupSuccess(true);
        setLoading(false);
        return;
      }

      setError(
        getAuthPasswordErrorMessage(error, "signup") ??
          error?.message ??
          "We could not sign you up right now. Please try again.",
      );
      setLoading(false);
    }
  }

  if (signupSuccess) {
    return (
      <AuthPageShell>
        <AuthBrandLogo />
        <div className="space-y-2 text-center">
          <h1 className="text-xl font-semibold tracking-tight">{title}</h1>
          <p className="text-sm text-muted-foreground">
            Check your email for a confirmation link.
          </p>
        </div>
        <div className="text-center text-sm">
          <Link href="/login" className="text-foreground underline underline-offset-4 hover:text-foreground/80">
            Back to sign in
          </Link>
        </div>
      </AuthPageShell>
    );
  }

  return (
    <AuthPageShell>
      <AuthBrandLogo />
      <div className="space-y-2 text-center">
        <h1 className="text-xl font-medium tracking-tight">{title}</h1>
        <p className="text-sm text-muted-foreground">{subtitle}</p>
      </div>

      {/* Google OAuth */}
      <Button
        type="button"
        variant={shouldUseGoogleFirst ? "default" : "outline"}
        className="w-full h-10 cursor-pointer hover:bg-accent transition-colors"
        onClick={handleGoogleSignIn}
        disabled={googleLoading || loading}
        data-testid="login-google"
      >
        <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
          <path
            d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
            fill="#4285F4"
          />
          <path
            d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            fill="#34A853"
          />
          <path
            d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            fill="#FBBC05"
          />
          <path
            d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            fill="#EA4335"
          />
        </svg>
        {googleButtonLabel}
      </Button>

      {showDivider && (
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-background px-2 text-muted-foreground">or</span>
          </div>
        </div>
      )}

      {canRevealPasswordForm && !showPasswordForm && (
        <div className="text-center text-sm">
          <button
            type="button"
            className="text-muted-foreground underline underline-offset-4 hover:text-foreground"
            onClick={() => setShowPasswordForm(true)}
          >
            Sign in with a password instead
          </button>
        </div>
      )}

      {showPasswordForm && (
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              placeholder="you@example.com"
              required
              autoComplete="email"
              data-testid="login-email"
            />
          </div>
          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="password">Password</Label>
              {mode === "login" && (
                <Link href="/forgot-password" className="text-sm text-foreground underline underline-offset-4 hover:text-foreground/80">
                  Forgot password?
                </Link>
              )}
            </div>
            <Input
              id="password"
              name="password"
              type="password"
              placeholder="••••••••"
              required
              minLength={mode === "signup" ? PASSWORD_MIN_LENGTH : undefined}
              autoComplete={mode === "login" ? "current-password" : "new-password"}
              data-testid="login-password"
            />
            {mode === "signup" && (
              <p className="text-xs text-muted-foreground">
                At least 12 characters, using at least 3 of: uppercase, lowercase, number, symbol.
              </p>
            )}
          </div>

          <CaptchaWidget
            ref={captchaRef}
            onVerify={setCaptchaToken}
            onExpire={() => setCaptchaToken("")}
          />

          <AuthErrorMessages serverError={serverError} error={error} />

          <button
            type="submit"
            disabled={loading || googleLoading}
            data-testid="login-submit"
            className="group relative w-full h-10 rounded-md bg-foreground text-background font-medium overflow-hidden cursor-pointer transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <div
              className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
              style={{
                backgroundImage: `repeating-linear-gradient(
                  45deg,
                  transparent,
                  transparent 8px,
                  rgba(255, 255, 255, 0.15) 8px,
                  rgba(255, 255, 255, 0.15) 16px
                )`,
                backgroundSize: "200% 200%",
                animation: "slideStripes 2s linear infinite",
              }}
            />
            <span className="relative z-10">
              {loading ? loadingLabel : submitLabel}
            </span>
          </button>
          <style>{`
            @keyframes slideStripes {
              0% { background-position: 0% 0%; }
              100% { background-position: 100% 100%; }
            }
          `}</style>
        </form>
      )}

      {!showPasswordForm && (
        <AuthErrorMessages serverError={serverError} error={error} />
      )}

      <p className="text-center text-sm text-muted-foreground">
        {altText}{" "}
        <Link href={altLink} className="text-foreground underline underline-offset-4 hover:text-foreground/80">
          {altLinkLabel}
        </Link>
      </p>
    </AuthPageShell>
  );
}
